import React, { useEffect, useState } from "react";
import { RenderMultiInputProps } from "../../../../types";
import {
  dispatchDeletedObjectIfExists,
  dispatchDeletedObjectsForAll,
} from "../../helpers";
import { useIntl } from "react-intl";
import { MultiInputWithTooltip } from "../../../common/Tooltip";
import styled, { css } from "styled-components";

const MultiInputWrapper = styled.div<{ $isDisabled: boolean }>`
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      [data-testid$="-value"] svg {
        display: none;
      }
    `}
`;

export default function RenderMultiInput({
  title,
  description,
  propertyKey,
  propertySubKey,
  nestedPropertySubKey,
  nestedRowIndex,
  inputValues,
  isRequired,
  isDisabled,
  error,
  message,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: Readonly<RenderMultiInputProps>): JSX.Element {
  const [inputValue, setInputValue] = useState<string>("");
  const [multiInputValues, setMultiInputValues] = useState<string[]>([]);
  const intl = useIntl();
  useEffect(() => {
    setMultiInputValues(inputValues.map((option) => option.value as string));
  }, [inputValues]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addValueToList(inputValue);
    }
  };

  const addValueToList = (value: string) => {
    if (value.trim()) {
      setMultiInputValues([...multiInputValues, value.trim()]);
      setInputValue("");

      dispatch({
        type: "ADD_MULTI_VALUED_FIELD",
        payload: {
          propertyKey,
          newFieldValue: value,
          propertySubKey: propertySubKey,
          nestedPropertySubKey,
          nestedRowIndex,
          selectedTabSectionKey,
        },
      });
    }
  };

  const handleRemoveInputValue = (index: number) => {
    if (isDisabled) return;
    const updatedValues = multiInputValues.filter((_, i) => i !== index);
    setMultiInputValues(updatedValues);
    if (propertySubKey) {
      dispatch({
        type: "REMOVE_MULTI_VALUED_FIELD",
        payload: {
          propertyKey,
          propertySubKey,
          rowIndex: index,
          nestedPropertySubKey,
          nestedRowIndex,
          selectedTabSectionKey,
        },
      });
    }

    dispatchDeletedObjectIfExists({
      propertyKey,
      propertySubKey,
      nestedRowIndex,
      index,
      rowData: rowDataReducerState.rowData,
      selectedTabSectionKey,
      dispatch,
    });
  };

  const handleClear = () => {
    if (isDisabled) return;
    setMultiInputValues([]);
    dispatch({
      type: "REMOVE_MULTI_VALUED_FIELD",
      payload: {
        propertyKey,
        propertySubKey,
        newFieldValue: null,
        nestedPropertySubKey,
        nestedRowIndex,
        selectedTabSectionKey,
      },
    });

    dispatchDeletedObjectsForAll({
      propertyKey,
      propertySubKey,
      nestedRowIndex,
      rowData: rowDataReducerState.rowData,
      selectedTabSectionKey,
      dispatch,
    });
  };

  return (
    <MultiInputWrapper $isDisabled={isDisabled}>
      <MultiInputWithTooltip
        dataTestId={`${propertyKey}-multi-input`}
        id={`${propertyKey}-multi-input`}
        label={title}
        value={inputValue}
        multiInputValues={multiInputValues}
        showClearIcon={multiInputValues.length > 0 && !isDisabled}
        onChange={handleInputChange}
        onClear={handleClear}
        onKeyPress={handleKeyPress}
        placeholder={isDisabled ? title : intl.formatMessage(
          { id: "placeholder.enter", defaultMessage: "Enter {item}" },
          { item: title },
        )}
        removeInputValue={handleRemoveInputValue}
        size="small"
        isRequired={isRequired && !isDisabled}
        isInvalid={error}
        isDisabled={isDisabled}
        errorMessage={message}
        tooltip={description}
      />
    </MultiInputWrapper>
  );
}
